/**
 * StudioSEO — AI-powered YouTube SEO Assistant.
 *
 * Flow: paste YouTube URL (or transcript) → server fetches transcript →
 * Gemini 3 Pro generates 3 title variants + 3 description variants +
 * 50 tags (tiered) + hashtags + chapters + thumbnail/pinned-comment hints.
 * Polls job status. Lets user pick a title/description, edit inline,
 * then apply to a Studio content row (ACID versioned write).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sparkles, Loader2, Youtube, ArrowLeft, AlertTriangle, Wand2,
  CheckCircle2, RefreshCw, Copy, Download, History, Send, Tag,
  ListChecks, Hash, MessageSquare, ImageIcon, Pencil, Check,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_LABEL = {
  queued: "Queued",
  fetching_transcript: "Fetching transcript",
  ai_generating: "Generating SEO with Gemini 3 Pro",
  complete: "Complete",
  failed: "Failed",
};

const STATUS_STEPS = [
  { key: "queued", label: "Queued", at: 5 },
  { key: "fetching_transcript", label: "Transcript", at: 20 },
  { key: "ai_generating", label: "AI generation", at: 60 },
  { key: "complete", label: "Done", at: 100 },
];

function CopyBtn({ text, testid }) {
  return (
    <button
      className="studio-btn-ghost"
      style={{ padding: "6px 10px", fontSize: 12 }}
      data-testid={testid}
      onClick={() => {
        navigator.clipboard?.writeText(text || "");
        toast.success("Copied");
      }}
    >
      <Copy size={12} /> Copy
    </button>
  );
}

function ScoreBar({ label, value }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const color = v >= 80 ? "#10b981" : v >= 60 ? "#a78bfa" : v >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
      <small style={{ color: "var(--st-text-soft)", minWidth: 70, textTransform: "capitalize" }}>{label}</small>
      <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden", minWidth: 60 }}>
        <div style={{ width: `${v}%`, height: "100%", background: color, transition: "width 0.4s" }} />
      </div>
      <strong style={{ minWidth: 28, textAlign: "right" }}>{v}</strong>
    </div>
  );
}

export default function StudioSEO() {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [manualTranscript, setManualTranscript] = useState("");
  const [hintTitle, setHintTitle] = useState("");
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [editingTitleIdx, setEditingTitleIdx] = useState(null);
  const [editingDescIdx, setEditingDescIdx] = useState(null);
  const [titleOverrides, setTitleOverrides] = useState({});
  const [descOverrides, setDescOverrides] = useState({});
  const [pickedTitleIdx, setPickedTitleIdx] = useState(0);
  const [pickedDescIdx, setPickedDescIdx] = useState(0);
  const [tagTier, setTagTier] = useState(15);
  const [contentList, setContentList] = useState([]);
  const [applyContentId, setApplyContentId] = useState("");
  const [applying, setApplying] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("input"); // "input" | "history"
  const stoppedRef = useRef(false);

  // Load channels
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/studio/channels`, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        const all = [...(r.data?.owned || []), ...(r.data?.shared || [])];
        setChannels(all);
        if (!channelId && all[0]) setChannelId(all[0].channel_id);
      })
      .catch((err) => alive && setError(err?.response?.data?.detail || err.message));
    loadHistory();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load content list for selected channel (for Apply)
  useEffect(() => {
    if (!channelId) return;
    let alive = true;
    axios.get(`${API}/api/studio/content/by-channel/${channelId}`, { withCredentials: true })
      .then((r) => { if (alive) setContentList(r.data?.items || r.data || []); })
      .catch(() => { if (alive) setContentList([]); });
    return () => { alive = false; };
  }, [channelId]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/studio/seo/history`, { withCredentials: true });
      setHistory(r.data?.jobs || []);
    } catch (e) { /* ignore */ }
  };

  // Polling — keyed by job id only; we stop ourselves when status terminal.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const jobId = job?._id;
    if (!jobId) return undefined;
    if (job?.status === "complete" || job?.status === "failed") return undefined;
    stoppedRef.current = false;
    setElapsed(0);
    const startedAt = Date.now();
    const tick = async () => {
      if (stoppedRef.current) return;
      try {
        const r = await axios.get(`${API}/api/studio/seo/job/${jobId}`, { withCredentials: true });
        if (stoppedRef.current) return;
        setJob(r.data);
        if (r.data?.status === "complete") {
          toast.success("SEO assets ready");
          loadHistory();
        } else if (r.data?.status === "failed") {
          toast.error(r.data?.error || "Job failed");
        }
      } catch (e) {
        // keep polling
      }
    };
    const id = setInterval(tick, 1200);
    const timer = setInterval(() => {
      if (!stoppedRef.current) setElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => { stoppedRef.current = true; clearInterval(id); clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?._id]);
  const polling = !!job && job.status !== "complete" && job.status !== "failed";

  const generate = async () => {
    setError(null);
    if (!youtubeUrl && !manualTranscript) {
      toast.error("Paste a YouTube URL or transcript");
      return;
    }
    try {
      const r = await axios.post(`${API}/api/studio/seo/generate`, {
        youtube_url: youtubeUrl || undefined,
        transcript: manualTranscript || undefined,
        hint_title: hintTitle || undefined,
        channel_id: channelId || undefined,
        provider: "gemini",
      }, { withCredentials: true });
      // Initial poll right away so user sees movement
      const jobId = r.data.job_id;
      setJob({ _id: jobId, status: r.data.status, progress: 5 });
      setEditingTitleIdx(null);
      setEditingDescIdx(null);
      setTitleOverrides({});
      setDescOverrides({});
      setPickedTitleIdx(0);
      setPickedDescIdx(0);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Generation failed";
      setError(msg);
      toast.error(msg);
    }
  };

  const result = job?.status === "complete" ? job.result : null;

  const titleText = (idx) =>
    titleOverrides[idx] !== undefined ? titleOverrides[idx] : (result?.titles?.[idx]?.text || "");
  const descText = (idx) =>
    descOverrides[idx] !== undefined ? descOverrides[idx] : (result?.descriptions?.[idx]?.text || "");

  const applyToContent = async () => {
    if (!job || !applyContentId) { toast.error("Pick a project to apply to"); return; }
    setApplying(true);
    try {
      // If user edited the picked title/description, write that first via the
      // ACID universal field-edit endpoint so the version trail has the *edited*
      // version (not the raw AI variant). Then call /apply with the index to
      // copy any remaining fields (tags, hashtags).
      const editedTitle = titleOverrides[pickedTitleIdx];
      const editedDesc = descOverrides[pickedDescIdx];
      if (editedTitle && editedTitle !== result.titles?.[pickedTitleIdx]?.text) {
        await axios.put(`${API}/api/studio/content/${applyContentId}/field`, {
          field: "title", value: editedTitle, change_summary: "SEO Assistant · edited title",
        }, { withCredentials: true });
      }
      if (editedDesc && editedDesc !== result.descriptions?.[pickedDescIdx]?.text) {
        await axios.put(`${API}/api/studio/content/${applyContentId}/field`, {
          field: "description", value: editedDesc, change_summary: "SEO Assistant · edited description",
        }, { withCredentials: true });
      }
      // Now call the bulk apply (handles tags + the non-edited fields too).
      const payload = {
        job_id: job._id,
        content_id: applyContentId,
        title_index: editedTitle ? null : pickedTitleIdx,
        description_index: editedDesc ? null : pickedDescIdx,
        apply_tags: true,
        tag_tier: tagTier,
      };
      const r = await axios.post(`${API}/api/studio/seo/apply`, payload, { withCredentials: true });
      const fields = r.data?.applied || [];
      toast.success(`Applied: ${fields.join(", ") || "nothing"}`);
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const downloadCsv = () => {
    if (!job?._id) return;
    window.open(`${API}/api/studio/seo/export/${job._id}.csv`, "_blank");
  };

  const reuseJob = async (jid) => {
    try {
      const r = await axios.get(`${API}/api/studio/seo/job/${jid}`, { withCredentials: true });
      setJob(r.data);
      setTab("input");
      setYoutubeUrl(r.data.youtube_url || "");
      setHintTitle(r.data.hint_title || "");
      setEditingTitleIdx(null);
      setEditingDescIdx(null);
      setTitleOverrides({});
      setDescOverrides({});
    } catch (e) { toast.error("Could not load job"); }
  };

  const stepIdx = STATUS_STEPS.findIndex((s) => s.key === job?.status);

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <Link to="/studio" className="studio-btn-ghost" data-testid="seo-back" style={{ marginBottom: 12 }}>
            <ArrowLeft size={14} /> Back to Studio
          </Link>
          <span className="studio-eyebrow">
            <Sparkles size={12} /> AI SEO Assistant · Gemini 3 Pro
          </span>
          <h1 className="studio-h1">
            One URL in. <span className="studio-h1-accent">A search-optimised package out.</span>
          </h1>
          <p className="studio-sub">
            Paste a YouTube URL — we&apos;ll pull the transcript, ask Gemini 3 Pro to engineer titles, descriptions,
            tags, hashtags and chapters that out-rank the niche. Edit, lock, and apply to any project
            with full ACID version history.
          </p>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              className={tab === "input" ? "studio-btn-primary" : "studio-btn-ghost"}
              onClick={() => setTab("input")}
              data-testid="seo-tab-input"
            >
              <Wand2 size={14} /> Generate
            </button>
            <button
              className={tab === "history" ? "studio-btn-primary" : "studio-btn-ghost"}
              onClick={() => { setTab("history"); loadHistory(); }}
              data-testid="seo-tab-history"
            >
              <History size={14} /> History ({history.length})
            </button>
          </div>
        </div>

        {error && (
          <div className="ac-error" data-testid="seo-error" style={{ marginBottom: 14 }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {tab === "input" && (
          <>
            <div className="studio-card" style={{ padding: 18 }}>
              <h3>1 · Source</h3>
              <div style={{ display: "grid", gap: 12 }}>
                <select
                  className="studio-input"
                  value={channelId}
                  onChange={(e) => setChannelId(e.target.value)}
                  data-testid="seo-channel-select"
                >
                  <option value="">Optional: link to a channel…</option>
                  {channels.map((c) => (
                    <option key={c.channel_id} value={c.channel_id}>{c.title}</option>
                  ))}
                </select>

                <div style={{ position: "relative" }}>
                  <Youtube size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#ef4444" }} />
                  <input
                    className="studio-input"
                    type="url"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    data-testid="seo-youtube-url"
                    style={{ paddingLeft: 36 }}
                  />
                </div>

                <details style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, padding: 10 }}>
                  <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--st-text-soft)" }}>
                    No public captions? Paste a transcript manually
                  </summary>
                  <textarea
                    className="studio-input"
                    rows={6}
                    placeholder="Paste raw transcript / script here…"
                    value={manualTranscript}
                    onChange={(e) => setManualTranscript(e.target.value)}
                    data-testid="seo-manual-transcript"
                    style={{ marginTop: 8, fontFamily: "inherit" }}
                  />
                  <input
                    className="studio-input"
                    placeholder="Optional: hint title / topic"
                    value={hintTitle}
                    onChange={(e) => setHintTitle(e.target.value)}
                    data-testid="seo-hint-title"
                    style={{ marginTop: 8 }}
                  />
                </details>

                <button
                  className="studio-btn-primary studio-btn-xl"
                  onClick={generate}
                  disabled={polling || (!youtubeUrl && !manualTranscript)}
                  data-testid="seo-generate-btn"
                  style={{ width: "100%" }}
                >
                  {polling ? <Loader2 size={16} className="ac-spin" /> : <Wand2 size={16} />}
                  {polling ? "Working…" : "Generate SEO"}
                </button>
              </div>
            </div>

            {/* Progress */}
            {job && (
              <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="seo-progress">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <strong>{STATUS_LABEL[job.status] || job.status}</strong>
                  <small className="studio-muted" data-testid="seo-elapsed">
                    {polling ? `${elapsed}s · ~15-25s typical` : `${job.progress || 0}%`}
                  </small>
                </div>
                <div style={{ height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
                  <div
                    style={{
                      width: `${job.progress || 0}%`,
                      height: "100%",
                      background: job.status === "failed" ? "#ef4444" : "linear-gradient(90deg, #a78bfa, #ec7c5e)",
                      transition: "width 0.5s",
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
                  {STATUS_STEPS.map((s, i) => {
                    const done = stepIdx > i || job.status === "complete";
                    const cur = stepIdx === i;
                    return (
                      <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: done || cur ? 1 : 0.4 }}>
                        {done ? <CheckCircle2 size={14} style={{ color: "#10b981" }} /> : cur ? <Loader2 size={14} className="ac-spin" /> : <span style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px solid currentColor" }} />}
                        {s.label}
                      </div>
                    );
                  })}
                </div>
                {job.status === "failed" && (
                  <div className="ac-error" style={{ marginTop: 12 }}>
                    <AlertTriangle size={14} /> {job.error || "Unknown error"}
                  </div>
                )}
              </div>
            )}

            {/* Results */}
            {result && (
              <>
                {/* Strategy summary */}
                <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="seo-strategy">
                  <h3>2 · Strategy</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                    <div><small className="studio-muted">Primary keyword</small><div><strong>{result.primary_keyword || "—"}</strong></div></div>
                    <div><small className="studio-muted">Search intent</small><div><strong style={{ textTransform: "capitalize" }}>{result.search_intent || "—"}</strong></div></div>
                    <div><small className="studio-muted">Target audience</small><div><strong>{result.target_audience || "—"}</strong></div></div>
                    <div><small className="studio-muted">Category</small><div><strong>{result.video_category || "—"}</strong></div></div>
                    <div><small className="studio-muted">AI confidence</small><div><strong>{result.ai_confidence ?? "—"}/100</strong></div></div>
                  </div>
                </div>

                {/* Titles */}
                <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="seo-titles">
                  <h3>3 · Title variants <small className="studio-muted">(pick one)</small></h3>
                  <div style={{ display: "grid", gap: 10 }}>
                    {(result.titles || []).map((t, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="studio-card"
                        style={{
                          padding: 14, margin: 0,
                          borderColor: pickedTitleIdx === i ? "rgba(167,139,250,0.5)" : undefined,
                          background: pickedTitleIdx === i ? "rgba(167,139,250,0.06)" : undefined,
                        }}
                        data-testid={`seo-title-${i}`}
                      >
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                          <button
                            type="button"
                            onClick={() => setPickedTitleIdx(i)}
                            data-testid={`seo-title-pick-${i}`}
                            aria-label={`Pick title variant ${i + 1}`}
                            style={{
                              marginTop: 4, width: 16, height: 16, borderRadius: "50%",
                              border: pickedTitleIdx === i ? "2px solid #a78bfa" : "2px solid rgba(255,255,255,0.25)",
                              background: pickedTitleIdx === i ? "#a78bfa" : "transparent",
                              cursor: "pointer", flexShrink: 0,
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                              <small className="studio-muted" style={{ textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5 }}>
                                {t.label?.replace(/_/g, " ") || `Variant ${i + 1}`}
                              </small>
                              <button
                                type="button"
                                className="studio-btn-ghost"
                                onClick={() => setEditingTitleIdx(editingTitleIdx === i ? null : i)}
                                data-testid={`seo-title-edit-toggle-${i}`}
                                style={{ padding: "4px 10px", fontSize: 11 }}
                              >
                                {editingTitleIdx === i ? <><Check size={11} /> Done</> : <><Pencil size={11} /> Edit</>}
                              </button>
                            </div>
                            {editingTitleIdx === i ? (
                              <textarea
                                className="studio-input"
                                rows={2}
                                value={titleText(i)}
                                onChange={(e) => setTitleOverrides((s) => ({ ...s, [i]: e.target.value }))}
                                data-testid={`seo-title-edit-${i}`}
                                style={{ marginTop: 6, fontWeight: 600 }}
                              />
                            ) : (
                              <div
                                style={{ marginTop: 4, fontWeight: 600, fontSize: 15 }}
                              >
                                {titleText(i)}
                              </div>
                            )}
                            <small className="studio-muted" style={{ fontSize: 11 }}>{titleText(i).length}/70 chars</small>
                            {t.scores && (
                              <div style={{ display: "grid", gap: 4, marginTop: 8, maxWidth: 380 }}>
                                {Object.entries(t.scores).map(([k, v]) => (
                                  <ScoreBar key={k} label={k} value={v} />
                                ))}
                              </div>
                            )}
                          </div>
                          <CopyBtn text={titleText(i)} testid={`seo-title-copy-${i}`} />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Descriptions */}
                <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="seo-descs">
                  <h3>4 · Description variants <small className="studio-muted">(pick one)</small></h3>
                  <div style={{ display: "grid", gap: 10 }}>
                    {(result.descriptions || []).map((d, i) => (
                      <div
                        key={i}
                        className="studio-card"
                        style={{
                          padding: 14, margin: 0,
                          borderColor: pickedDescIdx === i ? "rgba(167,139,250,0.5)" : undefined,
                          background: pickedDescIdx === i ? "rgba(167,139,250,0.06)" : undefined,
                        }}
                        data-testid={`seo-desc-${i}`}
                      >
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                          <button
                            type="button"
                            onClick={() => setPickedDescIdx(i)}
                            data-testid={`seo-desc-pick-${i}`}
                            aria-label={`Pick description variant ${i + 1}`}
                            style={{
                              marginTop: 4, width: 16, height: 16, borderRadius: "50%",
                              border: pickedDescIdx === i ? "2px solid #a78bfa" : "2px solid rgba(255,255,255,0.25)",
                              background: pickedDescIdx === i ? "#a78bfa" : "transparent",
                              cursor: "pointer", flexShrink: 0,
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                              <small className="studio-muted" style={{ textTransform: "uppercase", fontSize: 10 }}>
                                Variant {i + 1}
                              </small>
                              <button
                                type="button"
                                className="studio-btn-ghost"
                                onClick={() => setEditingDescIdx(editingDescIdx === i ? null : i)}
                                data-testid={`seo-desc-edit-toggle-${i}`}
                                style={{ padding: "4px 10px", fontSize: 11 }}
                              >
                                {editingDescIdx === i ? <><Check size={11} /> Done</> : <><Pencil size={11} /> Edit</>}
                              </button>
                            </div>
                            {editingDescIdx === i ? (
                              <textarea
                                className="studio-input"
                                rows={8}
                                value={descText(i)}
                                onChange={(e) => setDescOverrides((s) => ({ ...s, [i]: e.target.value }))}
                                data-testid={`seo-desc-edit-${i}`}
                                style={{ marginTop: 6 }}
                              />
                            ) : (
                              <div
                                style={{ marginTop: 4, fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }}
                              >
                                {descText(i)}
                              </div>
                            )}
                            <small className="studio-muted" style={{ fontSize: 11 }}>{descText(i).length} chars</small>
                            {d.scores && (
                              <div style={{ display: "grid", gap: 4, marginTop: 8, maxWidth: 380 }}>
                                {Object.entries(d.scores).map(([k, v]) => (
                                  <ScoreBar key={k} label={k} value={v} />
                                ))}
                              </div>
                            )}
                          </div>
                          <CopyBtn text={descText(i)} testid={`seo-desc-copy-${i}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="seo-tags">
                  <h3><Tag size={14} style={{ display: "inline", marginRight: 6 }} />5 · Tags</h3>
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    {[15, 30, 50].map((n) => (
                      <button
                        key={n}
                        className={tagTier === n ? "studio-btn-primary" : "studio-btn-ghost"}
                        onClick={() => setTagTier(n)}
                        data-testid={`seo-tag-tier-${n}`}
                        style={{ padding: "6px 14px", fontSize: 12 }}
                      >
                        Top {n}
                      </button>
                    ))}
                    <CopyBtn text={(result[`tags_top_${tagTier}`] || []).join(", ")} testid="seo-tags-copy" />
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(result[`tags_top_${tagTier}`] || []).map((tag, i) => (
                      <span key={i} className="studio-pill studio-pill--sm" data-testid={`seo-tag-${i}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Hashtags + Chapters + Misc */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginTop: 14 }}>
                  {result.hashtags?.length > 0 && (
                    <div className="studio-card" style={{ padding: 18 }} data-testid="seo-hashtags">
                      <h3><Hash size={14} style={{ display: "inline", marginRight: 6 }} />Hashtags</h3>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {result.hashtags.map((h, i) => (
                          <span key={i} className="studio-pill studio-pill--sm">{h}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.chapters?.length > 0 && (
                    <div className="studio-card" style={{ padding: 18 }} data-testid="seo-chapters">
                      <h3><ListChecks size={14} style={{ display: "inline", marginRight: 6 }} />Chapters</h3>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13, lineHeight: 1.7 }}>
                        {result.chapters.map((c, i) => (
                          <li key={i}><code style={{ background: "rgba(255,255,255,0.05)", padding: "1px 6px", borderRadius: 4 }}>{c.ts}</code> {c.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.thumbnail_text && (
                    <div className="studio-card" style={{ padding: 18 }} data-testid="seo-thumbnail-text">
                      <h3><ImageIcon size={14} style={{ display: "inline", marginRight: 6 }} />Thumbnail text</h3>
                      <p style={{ fontSize: 14, fontWeight: 600 }}>{result.thumbnail_text}</p>
                    </div>
                  )}

                  {result.pinned_comment && (
                    <div className="studio-card" style={{ padding: 18 }} data-testid="seo-pinned-comment">
                      <h3><MessageSquare size={14} style={{ display: "inline", marginRight: 6 }} />Pinned comment</h3>
                      <p style={{ fontSize: 13, lineHeight: 1.5 }}>{result.pinned_comment}</p>
                    </div>
                  )}
                </div>

                {/* Apply */}
                <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="seo-apply-panel">
                  <h3><Send size={14} style={{ display: "inline", marginRight: 6 }} />6 · Apply to project</h3>
                  <p className="studio-card-sub">
                    Writes the picked title, description and tag tier to a content row with full ACID
                    version history (the previous values stay restorable).
                  </p>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr auto auto", alignItems: "center" }}>
                    <select
                      className="studio-input"
                      value={applyContentId}
                      onChange={(e) => setApplyContentId(e.target.value)}
                      data-testid="seo-apply-content-select"
                    >
                      <option value="">Pick a project from channel…</option>
                      {contentList.map((c) => (
                        <option key={c._id || c.id} value={c._id || c.id}>
                          [{c.stage || "idea"}] {c.title || "Untitled"}
                        </option>
                      ))}
                    </select>
                    <button
                      className="studio-btn-primary"
                      onClick={applyToContent}
                      disabled={applying || !applyContentId}
                      data-testid="seo-apply-btn"
                    >
                      {applying ? <Loader2 size={14} className="ac-spin" /> : <Send size={14} />}
                      Apply
                    </button>
                    <button
                      className="studio-btn-ghost"
                      onClick={downloadCsv}
                      data-testid="seo-download-csv"
                    >
                      <Download size={14} /> CSV
                    </button>
                  </div>
                  {!contentList.length && channelId && (
                    <small className="studio-muted" style={{ marginTop: 8, display: "block" }}>
                      No projects in this channel yet. Add one from <Link to="/studio">Command Center</Link>.
                    </small>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <button
                    className="studio-btn-ghost"
                    onClick={generate}
                    disabled={polling}
                    data-testid="seo-regenerate-btn"
                  >
                    <RefreshCw size={14} /> Regenerate
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {tab === "history" && (
          <div className="studio-card" style={{ padding: 18 }} data-testid="seo-history">
            <h3>SEO generation history</h3>
            {!history.length && (
              <p className="studio-muted">No past jobs yet. Generate one to see it here.</p>
            )}
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {history.map((j) => (
                <li key={j._id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {j.video_meta?.title || j.youtube_url || j.hint_title || "Manual transcript"}
                    </div>
                    <small className="studio-muted">
                      {new Date(j.created_at).toLocaleString()} · status: <strong>{j.status}</strong>
                      {j.result?.primary_keyword ? ` · ${j.result.primary_keyword}` : ""}
                    </small>
                  </div>
                  <button
                    className="studio-btn-ghost"
                    onClick={() => reuseJob(j._id)}
                    data-testid={`seo-history-open-${j._id}`}
                    style={{ padding: "6px 10px", fontSize: 12 }}
                  >
                    Open
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
